import React, { useState } from "react";
import { useFetch, api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ShieldCheck, FileWarning } from "lucide-react";
import { PageHeader, Panel, StatusChip, KpiTile, LoadingState, DemoBadge } from "@/components/app/primitives";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LiveEvidencePanel } from "@/components/app/liveModulePanels";

const statusTone = (s) => ({ received: "validated", partial: "ai", missing: "danger", requested: "risk" }[s] || "neutral");
const sensTone = (s) => ({ high: "danger", medium: "risk", low: "neutral" }[s] || "neutral");

export default function EvidenceRoomPage() {
  const { data: items, loading, refetch } = useFetch("/evidence");
  const { data: overview } = useFetch("/overview");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (loading) return <LoadingState />;

  const filtered = (items || []).filter((i) => filter === "all" || (filter === "open" ? ["missing", "requested", "partial"].includes(i.status) : i.status === filter));
  const counts = overview?.counts;

  const simulateUpload = async () => {
    setUploading(true);
    try {
      const res = await api.post("/evidence/upload", { filename: `working-session-extract-${Date.now().toString().slice(-4)}.csv`, category: "Working Session Upload" });
      toast.success("Artifact intake recorded", { description: res.data.analysis_note });
      refetch();
    } catch (e) {
      toast.error("Upload simulation failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="evidence-page">
      <PageHeader
        kicker="Scene 1 - Intake after NDA"
        title="Secure Intake & Evidence Room"
        subtitle="The live document and data request register. Every item states why it matters and which decision it supports. Sensitivity flags govern transfer paths."
        right={
          <button onClick={simulateUpload} disabled={uploading} data-testid="evidence-upload-button"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors disabled:opacity-50">
            <Upload size={13} /> {uploading ? "Recording intake..." : "Simulate secure upload"}
          </button>
        }
      />

      <LiveEvidencePanel />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="rounded-xl border border-[--green] bg-[--green-soft] p-3.5" data-testid="nda-status-card">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[--green] font-semibold"><ShieldCheck size={13} /> NDA Status</div>
          <div className="font-display text-sm font-semibold text-[--tx-1] mt-1.5">Executed - Mutual</div>
          <div className="text-[11px] text-[--tx-3] mt-0.5">Secure exchange enabled</div>
        </div>
        <KpiTile label="Received" value={counts?.evidence_received ?? "..."} sub="Fully usable" tone="validated" />
        <KpiTile label="Partial" value={counts?.evidence_partial ?? "..."} sub="Workable with caveats" tone="ai" />
        <KpiTile label="Missing / requested" value={counts?.evidence_missing ?? "..."} sub="1 item blocks a pilot" tone="risk" />
        <KpiTile label="Evidence completeness" value={`${overview?.state?.evidence_completeness ?? "..."}%`} sub="Weighted by priority" tone="neutral" />
      </div>

      <div className="flex items-center gap-2 mb-3">
        {["all", "received", "partial", "open"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`evidence-filter-${f}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${filter === f ? "border-[--six-crimson] bg-[--six-crimson-soft] text-[--tx-1]" : "border-[--hairline] bg-[--panel-dark] text-[--tx-2] hover:text-[--tx-1]"}`}>
            {f === "all" ? "All items" : f === "open" ? "Needs attention" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <span className="ml-auto text-xs text-[--tx-3] font-mono">{filtered.length} items</span>
      </div>

      <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-left dense-table" data-testid="evidence-room-table">
            <thead className="bg-[--shell-2] sticky top-0">
              <tr className="border-b border-[--divider]">
                <th className="py-2.5 px-3">ID</th>
                <th className="py-2.5 px-3">Category / Item</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3">Sensitivity</th>
                <th className="py-2.5 px-3">Owner (role)</th>
                <th className="py-2.5 px-3">Priority</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} onClick={() => setSelected(item)} data-testid={`evidence-row-${item.id}`}
                  className="border-b border-[--divider] last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors">
                  <td className="py-2.5 px-3 font-mono text-[11px] text-[--tx-3]">{item.id}</td>
                  <td className="py-2.5 px-3">
                    <div className="text-[13px] font-medium text-[--tx-1]">{item.title}</div>
                    <div className="text-[11px] text-[--tx-3]">{item.category}</div>
                  </td>
                  <td className="py-2.5 px-3"><StatusChip tone={statusTone(item.status)}>{item.status}</StatusChip></td>
                  <td className="py-2.5 px-3"><StatusChip tone={sensTone(item.sensitivity)}>{item.sensitivity}</StatusChip></td>
                  <td className="py-2.5 px-3 text-xs text-[--tx-2]">{item.owner_role}</td>
                  <td className="py-2.5 px-3 font-mono text-[11px] text-[--tx-2]">{item.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[--shell-2] border-[--hairline] w-full sm:max-w-md overflow-y-auto thin-scroll" data-testid="evidence-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[--tx-1] font-display flex items-center gap-2">
                  <span className="font-mono text-xs text-[--tx-3]">{selected.id}</span> {selected.title}
                </SheetTitle>
                <SheetDescription className="text-[--tx-3] text-xs">{selected.category} · {selected.format}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="flex flex-wrap gap-2">
                  <StatusChip tone={statusTone(selected.status)}>{selected.status}</StatusChip>
                  <StatusChip tone={sensTone(selected.sensitivity)}>sensitivity: {selected.sensitivity}</StatusChip>
                  <StatusChip tone="neutral">{selected.priority}</StatusChip>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">What we asked for</div>
                  <p className="text-[13px] text-[--tx-2] leading-relaxed">{selected.description}</p>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Why it matters</div>
                  <p className="text-[13px] text-[--tx-2] leading-relaxed">{selected.why_it_matters}</p>
                </div>
                {selected.gap_note && (
                  <div className="rounded-lg border border-[--amber] bg-[--amber-soft] p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[--amber] mb-1"><FileWarning size={11} /> Gap note</div>
                    <p className="text-xs text-[--tx-2] leading-relaxed">{selected.gap_note}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[--tx-3] mb-0.5">Owner role</div><div className="text-[--tx-1] font-medium">{selected.owner_role}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">Received</div><div className="text-[--tx-1] font-medium">{selected.received_date ? new Date(selected.received_date).toLocaleDateString() : "Not received"}</div></div>
                </div>
                <DemoBadge />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
